/**
 * SectionConfigPanel — Dynamic config editor for a selected section.
 *
 * Reads the widget schema from WIDGET_REGISTRY and auto-generates
 * appropriate controls for props, slots, and appearance.
 *
 * For hybrid sections (blocks + structural props), renders grouped layout:
 * Data Settings → Header Content (block list) → Options → Appearance
 */

import { useState, useCallback, useMemo } from 'react'
import { ArrowLeft, Type, ImageIcon, MousePointerClick, Minus, Space, Sparkles, ChevronDown, Search, LayoutGrid, Globe, Phone, RotateCcw, Layers, Heading, AlignLeft, CaseSensitive } from 'lucide-react'
import {
  WIDGET_REGISTRY,
  findSectionByEditorId,
  type SectionEnvelope,
  type SlotValue,
  type BlockNode,
  type RichTextBlockContent,
} from '@zaviago/storefront-config'
import type { PropDefinition, SlotDefinition } from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'
import type { SelectedBlockInfo } from './useBlockSelection'
import { SectionBreadcrumb } from './SectionBreadcrumb'
import {
  StringControl,
  NumberControl,
  BooleanControl,
  EnumControl,
  ColorControl,
  ImagePropControl,
  TextSlotControl,
  ImageSlotControl,
  ButtonSlotControl,
  CollectionControl,
  IconControl,
  CollectionListControl,
} from './controls'
import { AppearanceSection } from './AppearanceSection'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SectionConfigPanelProps {
  section: EditorSection
  onUpdate: (patch: Partial<SectionEnvelope>) => void
  onBack: () => void
  /** Active breakpoint from DeviceToggle — highlights the matching row in responsive controls */
  activeBreakpoint?: 'desktop' | 'tablet' | 'mobile'
  /** Fires when user edits a breakpoint row — syncs DeviceToggle */
  onBreakpointEdit?: (breakpoint: 'desktop' | 'tablet' | 'mobile') => void
  /** Full sections tree for breadcrumb path display */
  sections?: EditorSection[]
  /** Navigate to a section (breadcrumb click) */
  onSelectSection?: (editorId: string) => void
  /** Called when a block row is clicked in hybrid mode — triggers block selection */
  onBlockSelect?: (info: SelectedBlockInfo) => void
  /** Called to reset blocks to their default layout (from WIDGET_REGISTRY) */
  onResetToDefault?: (sectionPath: string) => void
}

// ---------------------------------------------------------------------------
// Block type icon map
// ---------------------------------------------------------------------------

const BLOCK_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  text: Type,
  image: ImageIcon,
  button: MousePointerClick,
  divider: Minus,
  spacer: Space,
  icon: Sparkles,
  'search-bar': Search,
  'category-nav': LayoutGrid,
  'social-link': Globe,
  'contact-item': Phone,
  'rich-text': Layers,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Humanize a type key like "product-grid" → "Product Grid" */
function humanizeType(type: string): string {
  return type
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Get a display label for a block based on its content type */
function getBlockLabel(block: BlockNode): string {
  switch (block.content.type) {
    case 'text':
      return (block.content.value || 'Empty text').slice(0, 40)
    case 'rich-text':
      return `Rich Text (${block.content.items.length} layers)`
    case 'button':
      return (block.content.label || 'Button').slice(0, 40)
    case 'image':
      return block.content.alt || 'Image'
    default:
      return humanizeType(block.type)
  }
}

/** Icon map for rich-text sub-item roles */
const RICH_TEXT_ROLE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  heading: Heading,
  body: AlignLeft,
  caption: CaseSensitive,
}

/** Render the appropriate control for a PropDefinition */
function renderPropControl(
  key: string,
  def: PropDefinition,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  const label = def.label ?? key

  switch (def.type) {
    case 'string':
      return (
        <StringControl
          key={key}
          value={String(value ?? def.default ?? '')}
          onChange={(v) => onChange(v)}
          label={label}
          maxLength={def.maxLength}
        />
      )
    case 'number':
      return (
        <NumberControl
          key={key}
          value={Number(value ?? def.default ?? 0)}
          onChange={(v) => onChange(v)}
          label={label}
          min={def.min}
          max={def.max}
        />
      )
    case 'boolean':
      return (
        <BooleanControl
          key={key}
          value={Boolean(value ?? def.default ?? false)}
          onChange={(v) => onChange(v)}
          label={label}
        />
      )
    case 'enum':
      return (
        <EnumControl
          key={key}
          value={(value ?? def.default ?? def.options[0]) as string | number}
          onChange={(v) => onChange(v)}
          label={label}
          options={def.options}
        />
      )
    case 'link':
      return (
        <StringControl
          key={key}
          value={String(value ?? def.default ?? '')}
          onChange={(v) => onChange(v)}
          label={label}
          placeholder="https://..."
        />
      )
    case 'collection':
      return (
        <CollectionControl
          key={key}
          value={String(value ?? def.default ?? '')}
          onChange={(v) => onChange(v)}
          label={label}
        />
      )
    case 'icon':
      return (
        <IconControl
          key={key}
          value={String(value ?? def.default ?? '')}
          onChange={(v) => onChange(v)}
          label={label}
        />
      )
    case 'collection-list':
      return (
        <CollectionListControl
          key={key}
          value={String(value ?? def.default ?? '[]')}
          onChange={(v) => onChange(v)}
          label={label}
        />
      )
    case 'color':
      return (
        <ColorControl
          key={key}
          value={String(value ?? def.default ?? '')}
          onChange={(v) => onChange(v)}
          label={label}
        />
      )
    case 'image':
      return (
        <ImagePropControl
          key={key}
          value={String(value ?? def.default ?? '')}
          onChange={(v) => onChange(v)}
          label={label}
        />
      )
    default:
      return null
  }
}

/** Render the appropriate control for a SlotDefinition */
function renderSlotControl(
  key: string,
  def: SlotDefinition,
  slotValue: SlotValue | undefined,
  onChange: (v: SlotValue) => void,
) {
  const label = def.label ?? key
  const fallbackSlot: SlotValue = slotValue ?? ''

  switch (def.type) {
    case 'text':
      return (
        <TextSlotControl
          key={`slot-${key}`}
          slotValue={fallbackSlot}
          onChange={onChange}
          label={label}
        />
      )
    case 'image':
      return (
        <ImageSlotControl
          key={`slot-${key}`}
          slotValue={fallbackSlot}
          onChange={onChange}
          label={label}
        />
      )
    case 'button':
      return (
        <ButtonSlotControl
          key={`slot-${key}`}
          slotValue={fallbackSlot}
          onChange={onChange}
          slotLabel={label}
        />
      )
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Sub-components for hybrid mode
// ---------------------------------------------------------------------------

/** Collapsible sidebar group with consistent styling */
function SidebarGroup({
  title,
  defaultOpen = true,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="mb-4">
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-[13px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600">
        {title}
        <ChevronDown className="h-3.5 w-3.5 transition-transform [[data-state=closed]_&]:-rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-4 pb-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Clickable block row in the hybrid "Header Content" list — expands for rich-text sub-items */
function BlockListRow({ block, onClick }: { block: BlockNode; onClick: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = BLOCK_ICON[block.type] ?? Type
  const label = getBlockLabel(block)
  const isRichText = block.content.type === 'rich-text'
  const items = isRichText ? (block.content as RichTextBlockContent).items : []

  return (
    <div>
      <button
        onClick={(e) => {
          if (isRichText) {
            e.stopPropagation()
            setExpanded((v) => !v)
          }
          onClick()
        }}
        className="flex w-full items-center gap-2 rounded-xs px-3 py-2 text-left text-sm hover:bg-gray-100"
      >
        {/* Expand chevron for rich-text blocks */}
        {isRichText ? (
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`}
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Icon className="h-4 w-4 shrink-0 text-gray-400" />
        <span className="truncate text-gray-700">{label}</span>
        {isRichText && (
          <span className="ml-auto rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-400">{items.length}</span>
        )}
      </button>

      {/* Rich-text sub-item layers */}
      {isRichText && expanded && items.length > 0 && (
        <div className="ml-5 border-l border-gray-200 pl-2">
          {items.map((item, idx) => {
            const RoleIcon = RICH_TEXT_ROLE_ICON[item.role] ?? Type
            const roleLabel = item.role.charAt(0).toUpperCase() + item.role.slice(1)
            const preview = (item.value || `Empty ${item.role}`).slice(0, 30)
            return (
              <button
                key={idx}
                onClick={onClick}
                className="flex w-full items-center gap-2 rounded-xs px-2 py-1.5 text-left text-xs hover:bg-gray-50"
              >
                <RoleIcon className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                <span className="shrink-0 font-medium text-gray-500">{roleLabel}</span>
                <span className="truncate text-gray-400">{preview}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SectionConfigPanel({
  section,
  onUpdate,
  onBack,
  activeBreakpoint,
  onBreakpointEdit,
  sections,
  onSelectSection,
  onBlockSelect,
  onResetToDefault,
}: SectionConfigPanelProps) {
  const widget = WIDGET_REGISTRY[section.type]
  const schema = widget?.schema
  const widgetLabel = schema?.label ?? humanizeType(section.type)

  // Compute which prop keys also exist in slots (collision set)
  const slotKeys = useMemo(
    () => new Set(Object.keys(schema?.slots ?? {})),
    [schema?.slots],
  )

  // Handler: update a single prop value
  const handlePropChange = useCallback(
    (key: string, value: unknown) => {
      const patch: Partial<SectionEnvelope> = { props: { [key]: value } }
      // If this key also exists in slots, sync the plain text value there too
      if (slotKeys.has(key)) {
        const existingSlot = section.slots?.[key]
        let updatedSlot: SlotValue
        if (existingSlot && typeof existingSlot !== 'string' && 'value' in existingSlot) {
          updatedSlot = { ...existingSlot, value: String(value) }
        } else {
          updatedSlot = { value: String(value) }
        }
        patch.slots = { [key]: updatedSlot }
      }
      onUpdate(patch)
    },
    [onUpdate, slotKeys, section.slots],
  )

  // Handler: update a slot value
  const handleSlotChange = useCallback(
    (key: string, slotValue: SlotValue) => {
      const patch: Partial<SectionEnvelope> = { slots: { [key]: slotValue } }
      // If this key also exists in props, sync the plain text value there too
      if (section.props[key] !== undefined || schema?.props[key]) {
        const plainText =
          typeof slotValue === 'string'
            ? slotValue
            : 'value' in slotValue
              ? slotValue.value
              : 'label' in slotValue
                ? slotValue.label
                : ''
        patch.props = { [key]: plainText }
      }
      onUpdate(patch)
    },
    [onUpdate, section.props, schema?.props],
  )

  // Handler: update an appearance prop (desktop value via _-prefixed key)
  const handleAppearanceChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate({ props: { [key]: value } })
    },
    [onUpdate],
  )

  // Handler: set a responsive override (tablet/mobile)
  const handleResponsiveChange = useCallback(
    (breakpoint: 'tablet' | 'mobile', key: string, value: unknown) => {
      onUpdate({
        appearance: {
          responsive: {
            [breakpoint]: { [key]: value },
          },
        },
      } as Partial<SectionEnvelope>)
    },
    [onUpdate],
  )

  // Handler: clear a responsive override (sends null → deepMergeAppearance deletes)
  const handleClearResponsive = useCallback(
    (breakpoint: 'tablet' | 'mobile', key: string) => {
      onUpdate({
        appearance: {
          responsive: {
            [breakpoint]: { [key]: null },
          },
        },
      } as Partial<SectionEnvelope>)
    },
    [onUpdate],
  )

  // Handler: reset blocks to default
  const handleReset = useCallback(() => {
    if (!onResetToDefault || !sections) return
    const lookup = findSectionByEditorId(sections, section._editorId)
    if (!lookup) return
    onResetToDefault(lookup.path.join('.'))
  }, [onResetToDefault, sections, section._editorId])

  // Handler: block row click → dispatch synthetic block selection
  const handleBlockRowClick = useCallback(
    (block: BlockNode) => {
      if (!onBlockSelect || !sections) return
      const lookup = findSectionByEditorId(sections, section._editorId)
      if (!lookup) return
      const sectionPath = lookup.path.join('.')
      onBlockSelect({
        sectionPath,
        blockId: block.id,
        blockType: block.content.type,
        content: block.content,
        widthPct: block.layout?.widthPct ?? 100,
      })
    },
    [onBlockSelect, sections, section._editorId],
  )

  if (!schema) {
    return (
      <div className="p-4">
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <p className="text-sm text-gray-400">Unknown section type: {section.type}</p>
      </div>
    )
  }

  const slotEntries = Object.entries(schema.slots ?? {}) as [string, SlotDefinition][]
  // Filter out props that start with _ (appearance) or collide with slots
  const propEntries = (Object.entries(schema.props) as [string, PropDefinition][]).filter(
    ([key]) => !key.startsWith('_') && !slotKeys.has(key),
  )

  // Hybrid detection: section has blocks AND structural props
  const hasBlocks = (section.blocks?.length ?? 0) > 0
  const hasStructuralProps = propEntries.some(([, def]) => def.structural)
  const isHybrid = hasBlocks && hasStructuralProps

  // Split props for hybrid mode
  const structuralProps = isHybrid ? propEntries.filter(([, def]) => def.structural) : []
  const optionProps = isHybrid ? propEntries.filter(([, def]) => !def.structural) : []

  // Shared header (back button, breadcrumb, title)
  const header = (
    <>
      <button
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" /> Back to sections
      </button>

      {sections && onSelectSection && (
        <SectionBreadcrumb
          sections={sections}
          editorId={section._editorId}
          onSelect={onSelectSection}
        />
      )}

      <h2 className="mb-4 text-base font-semibold text-gray-900">{widgetLabel}</h2>
    </>
  )

  // Shared appearance section
  const appearance = (
    <AppearanceSection
      section={section}
      onAppearanceChange={handleAppearanceChange}
      onResponsiveChange={handleResponsiveChange}
      onClearResponsive={handleClearResponsive}
      activeBreakpoint={activeBreakpoint}
      onBreakpointEdit={onBreakpointEdit}
    />
  )

  // ─── Hybrid layout: Data Settings + Header Content + Options + Appearance ───
  if (isHybrid) {
    return (
      <div className="p-4">
        {header}

        {/* Data Settings group — structural props */}
        <SidebarGroup title="Data Settings">
          {structuralProps.map(([key, def]) =>
            renderPropControl(
              key,
              def,
              section.props[key],
              (v) => handlePropChange(key, v),
            ),
          )}
        </SidebarGroup>

        {/* Block Content — clickable block list with dynamic title */}
        <SidebarGroup title={section.type.includes('header') ? 'Header Content' : section.type.includes('footer') ? 'Footer Content' : 'Block Content'}>
          <div className="-mx-1 space-y-1">
            {(section.blocks ?? []).map((block) => (
              <BlockListRow
                key={block.id}
                block={block}
                onClick={() => handleBlockRowClick(block)}
              />
            ))}
          </div>
          {/* Reset to Default button */}
          {onResetToDefault && (
            <button
              onClick={handleReset}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xs border border-gray-200 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            >
              <RotateCcw className="h-3 w-3" />
              Reset to default
            </button>
          )}
        </SidebarGroup>

        {/* Options group — non-structural props (skip if empty) */}
        {optionProps.length > 0 && (
          <SidebarGroup title="Options" defaultOpen={false}>
            {optionProps.map(([key, def]) =>
              renderPropControl(
                key,
                def,
                section.props[key],
                (v) => handlePropChange(key, v),
              ),
            )}
          </SidebarGroup>
        )}

        {/* Appearance section */}
        {appearance}
      </div>
    )
  }

  // ─── Standard flat layout (non-hybrid sections) ────────────────────────────
  const hasContent = slotEntries.length > 0 || propEntries.length > 0

  return (
    <div className="p-4">
      {header}

      {/* Content section */}
      <div className="mb-4">
        <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-gray-400">
          Content
        </h3>

        {!hasContent ? (
          <p className="text-sm text-gray-400">
            This section has no configurable content.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Slot editors first */}
            {slotEntries.map(([key, def]) =>
              renderSlotControl(
                key,
                def,
                section.slots?.[key],
                (v) => handleSlotChange(key, v),
              ),
            )}

            {/* Prop editors (excluding _ prefix and slot collisions) */}
            {propEntries.map(([key, def]) =>
              renderPropControl(
                key,
                def,
                section.props[key],
                (v) => handlePropChange(key, v),
              ),
            )}
          </div>
        )}
      </div>

      {/* Appearance section */}
      {appearance}
    </div>
  )
}
